import { useEffect, useState } from "react";
import { CloudMast } from "./CloudMast";
import { RetentionNote } from "./RetentionNote";
import { isStagingHost } from "../lib/host";

// Full-screen completion bookend. Once the encrypted viewer is in the
// user's Downloads folder the wizard chrome falls away and we land here —
// the journey opened full-screen (Landing/Agreement) and closes the same
// way. This screen holds the ONLY copy of the passphrase, so it is the
// highest-stakes surface in the product: the file + passphrase are the
// hero. Safety leans on the prominent always-visible passphrase, the "only
// key" warning, and the kept-for-7-days come-back email — not a self-attested
// ack gate (removed: it had no teeth, since closing the tab bypassed it anyway).

type Props = {
  viewer: {
    sizeBytes: number;
    passphrase: string;
    // True when THIS SESSION holds no key to show (lib/viewer-state): the
    // download was keyless (key delivered once, at submit — audit B7) and
    // the submit-time passphrase isn't in memory. The normal state for any
    // deep-link download, not just re-downloads.
    consumed?: boolean;
  };
  onClose: () => void;
  // Re-fetch the file (e.g. the save dialog got dismissed). The server re-serves
  // the same encrypted bundle; the passphrase was shown once and isn't repeated,
  // so this flips the screen into its "consumed" state.
  onRedownload?: () => void;
  redownloading?: boolean;
  // The address the come-back link was emailed to (shown in the reassurance note).
  backupEmail?: string;
  /** True when a come-back email was sent (server come-back email on + an address
   *  on file). Shows the "saved N days + emailed a link" reassurance. */
  backupEmailed?: boolean;
  /** The retention window in days (from /healthz result_retention_days). */
  retentionDays?: number;
  /** Downloaded onto a phone/tablet (deep-link escape hatch). The file only
   *  opens on a computer, so the lede + open-it steps switch to move-it-
   *  to-your-computer instructions instead of "Double-click in Downloads". */
  mobile?: boolean;
};

function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function DoneScreen({
  viewer,
  onClose,
  onRedownload,
  redownloading,
  backupEmail,
  backupEmailed,
  retentionDays,
  mobile,
}: Props) {
  const [copied, setCopied] = useState(false);
  // Leaving this page loses the only on-screen copy of the passphrase, so we
  // confirm before navigating away — but only when the passphrase is actually
  // shown (a consumed re-download has nothing to lose here, so Done just goes).
  const [confirmLeave, setConfirmLeave] = useState(false);

  function handleDone() {
    if (viewer.consumed) onClose();
    else setConfirmLeave(true);
  }

  useEffect(() => {
    if (!confirmLeave) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setConfirmLeave(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [confirmLeave]);

  async function copyPassphrase() {
    try {
      await navigator.clipboard.writeText(viewer.passphrase);
      setCopied(true);
    } catch {
      // Clipboard blocked — the passphrase is still on screen to copy by hand.
    }
  }

  return (
    <main className="landing done-screen">
     <div className="landing-wrap">
      <CloudMast />

      <section className="done-hero">
        <div className="landing-eyebrow">You're done</div>
        <h1 className="landing-title">Your analysis is ready.</h1>
        <p className="landing-lede">
          {/* "consumed" = this session holds no key (every deep-link download,
              not just repeats) — never claim "downloaded before"; point at the
              submit-time passphrase instead. */}
          {mobile
            ? "Your case is saved to this phone as one file. Keep it somewhere safe with the passphrase you saved when you started the analysis — together they unlock it."
            : viewer.consumed
              ? "Your case is saved as one file on your computer. Keep it somewhere safe with the passphrase you saved when you started the analysis — together they unlock it."
              : "Your case is saved as one file on your computer. Keep it — and the passphrase below — somewhere safe. Only your passphrase can open it."}
        </p>

        <div className="done-keep">
          <div className="done-file">
            <span className="done-file-icon" aria-hidden="true">📦</span>
            <div>
              <code className="filename">aurora-analysis.zip</code>
              <span className="done-file-meta">
                {formatSize(viewer.sizeBytes)} ·{" "}
                {mobile ? "saved to this phone" : "in your Downloads folder"}
              </span>
              <span className="done-file-browser">
                Your analysis plus a short readme — opens in any web browser
              </span>
            </div>
          </div>

          {viewer.consumed ? (
            <p className="done-pass-warn">
              Your passphrase was shown once — when you started the analysis.
              Use the one you saved then to open this file. We don't keep a
              copy and can't show it again.
            </p>
          ) : (
            <>
              <div className="done-pass">
                <span className="done-pass-label">Passphrase</span>
                <code className="done-pass-value">{viewer.passphrase}</code>
                <button type="button" className="ghost done-pass-copy" onClick={copyPassphrase}>
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
              <p className="done-pass-warn">
                Write this down. It's the only key to your file, and we can't
                recover it for you.
              </p>
            </>
          )}
          {backupEmailed && (
            <RetentionNote
              phase="post"
              retentionDays={retentionDays ?? 7}
              email={backupEmail}
              className="done-recovery"
            />
          )}
        </div>

        <div className="done-cols">
          <div className="done-col">
            <div className="landing-eyebrow">To open it</div>
            {mobile ? (
              <ol className="done-steps">
                <li>
                  Get the file to your computer — AirDrop it, email it to
                  yourself, or save it to your cloud storage.
                </li>
                <li>
                  Open the zip there — inside is your analysis and a short
                  readme. Open the analysis, enter your passphrase, and your
                  case opens, no internet needed.
                </li>
              </ol>
            ) : (
              <ol className="done-steps">
                <li>
                  Open <code className="filename">aurora-analysis.zip</code> in
                  your Downloads folder — inside is your analysis and a short
                  readme.
                </li>
                <li>
                  Follow the readme: open the analysis and enter your
                  passphrase. Your case opens, no internet needed.
                </li>
              </ol>
            )}
          </div>
          <div className="done-col">
            <div className="landing-eyebrow">What's next</div>
            <p className="done-next">
              Take the file to your attorney or advocate. Aurora names patterns
              and cites the messages they came from — it isn't legal advice and
              doesn't decide anything.
            </p>
          </div>
        </div>

        <div className="done-actions">
          {onRedownload && (
            <button
              type="button"
              className="ghost done-redownload"
              onClick={onRedownload}
              disabled={redownloading}
            >
              {redownloading ? "Downloading…" : "Download the file again"}
            </button>
          )}
          <button
            type="button"
            className="apply-submit"
            onClick={handleDone}
          >
            {isStagingHost()
              ? "Done — start fresh →"
              : "Done — return to plaintosee.cc →"}
          </button>
        </div>
      </section>

      <footer className="landing-foot">
        <div className="landing-foot-left">Aurora · Built independently.</div>
        <div className="landing-foot-right">&copy; 2026 Aurora</div>
      </footer>
     </div>

      {confirmLeave && (
        <div
          className="leave-warn-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leave-warn-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmLeave(false);
          }}
        >
          <div className="leave-warn">
            <h2 id="leave-warn-title">Did you save your passphrase?</h2>
            <p>
              It's the only key to your file, and it appears only on this page.
              We can't recover it — once you leave, it's gone for good. Make sure
              you've written it down or copied it before you go.
            </p>
            <div className="leave-warn-actions">
              <button
                type="button"
                className="ghost"
                onClick={() => setConfirmLeave(false)}
              >
                ← Go back
              </button>
              <button type="button" className="apply-submit" onClick={onClose}>
                I've saved it — leave →
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
